import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  createPreviewActions,
  ShareablePreview,
} from "@/core/components/shared/preview-action";
import { Label } from "@/core/components/ui/label";
import { Textarea } from "@/core/components/ui/textarea";
import { formatMinorAmount } from "@/core/utils/func/money-minor";
import { useFeeEngineTranslation } from "@/features/fee-engine/i18n/use-fee-engine-translation";
import type { FeeRuleRow } from "@/features/fee-engine/components/fee-rules/fee-rules-columns";
import { createApprovalReasonSchema } from "@/features/shared/schemas/maker-checker.schema";

/**
 * Preview d'activation d'une règle de frais — action SENSIBLE (guide §5.4) :
 * ne peut jamais être exécutée directement. Le confirm ne déclenche plus
 * `/activate` mais crée une demande d'approbation FEE_RULE_CHANGE
 * (maker-checker) — voir `FeeRuleRowActions`, qui porte la mutation.
 *
 * Le motif (`reason`) est obligatoire : il est affiché à l'approbateur dans
 * la file d'approbations. La ShareablePreview affiche le récapitulatif ET
 * une section frais indicative (fixedValue, min/max si présents).
 */
export function FeeRuleActivateDialog({
  feeRule,
  open,
  onOpenChange,
  onConfirm,
  pending,
}: {
  feeRule: FeeRuleRow;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Confirmé avec le motif métier de la demande d'approbation. */
  onConfirm: (reason: string) => void;
  pending: boolean;
}) {
  const { t } = useFeeEngineTranslation();
  const { i18n } = useTranslation();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- i18n.language : schéma zod traduit reconstruit sur changement de langue (règle projet).
  const reasonSchema = useMemo(() => createApprovalReasonSchema(), [i18n.language]);
  const [reason, setReason] = useState("");
  const [reasonError, setReasonError] = useState<string | undefined>();

  if (!open) return null;

  const fees = [
    ...(feeRule.percentageValue != null
      ? [
          {
            label: `${feeRule.percentageValue}%`,
            amount: 0,
            currency: feeRule.currency,
          },
        ]
      : []),
    ...(feeRule.fixedValueMinor != null && feeRule.fixedValueMinor > 0
      ? [
          {
            label: t("fees.rules.form.fixedValue"),
            amount: (feeRule.fixedValueMinor ?? 0) / 100,
            currency: feeRule.currency,
          },
        ]
      : []),
  ];

  const totalMinor =
    feeRule.fixedValueMinor ?? feeRule.maxFeeMinor ?? feeRule.minFeeMinor ?? 0;

  const handleConfirm = () => {
    const parsed = reasonSchema.safeParse({ reason });
    if (!parsed.success) {
      setReasonError(parsed.error.issues[0]?.message);
      return;
    }
    setReasonError(undefined);
    onConfirm(parsed.data.reason.trim());
  };

  return (
    <ShareablePreview
      data={{
        ...feeRule,
        // Enrichissement d'affichage : format lisible du frais fixe / plancher / plafond.
        fixedValueFormatted:
          feeRule.fixedValueMinor != null
            ? formatMinorAmount(feeRule.fixedValueMinor, feeRule.currency)
            : null,
        minFeeFormatted:
          feeRule.minFeeMinor != null
            ? formatMinorAmount(feeRule.minFeeMinor, feeRule.currency)
            : null,
        maxFeeFormatted:
          feeRule.maxFeeMinor != null
            ? formatMinorAmount(feeRule.maxFeeMinor, feeRule.currency)
            : null,
      }}
      title={t("fees.rules.preview.activateTitle")}
      description={t("fees.rules.preview.activateDescription")}
      mode="user"
      fees={fees}
      total={{
        label: t("fees.rules.preview.totalLabel"),
        amount: totalMinor / 100,
        currency: feeRule.currency,
      }}
      defaultOpen
      onOpenChange={(isOpen) => {
        if (!isOpen && !pending) onOpenChange(false);
      }}
      maxHeight="90vh"
      customView={() => (
        <div className="space-y-3">
          <div>
            <Label htmlFor="approvalReason">
              {t("fees.rules.preview.reason")}
            </Label>
            <Textarea
              id="approvalReason"
              className="mt-1"
              placeholder={t("fees.rules.preview.reasonPlaceholder")}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
            {reasonError ? (
              <p className="text-sm text-destructive">{reasonError}</p>
            ) : null}
          </div>
        </div>
      )}
      actions={createPreviewActions({
        onConfirm: handleConfirm,
        onCancel: () => onOpenChange(false),
        t,
        confirmLoading: pending,
        withIndicatorOnConfirm: true,
      })}
    />
  );
}
